import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router";
import {
  Search,
  FileText,
  Palette,
  Rocket,
  Download,
  CreditCard,
  ExternalLink,
  Eye,
  Sun,
  Moon,
  X,
  BookOpen,
  User,
} from "lucide-react";
import { ProfileDraftContext } from "@/context/ProfileDraftContext";

interface CommandItem {
  id: string;
  title: string;
  category: "Navigation" | "Jump to Section" | "Actions";
  icon: React.ElementType;
  shortcut?: string;
  onSelect: () => void;
}

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Safely read editor context if mounted inside editor (null when on billing/cv/admin pages)
  const editorContext = useContext(ProfileDraftContext);

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Build command list
  const dynamicSectionCommands: CommandItem[] = (editorContext?.draft?.sections || []).map((sec) => ({
    id: `sec-${sec.id}`,
    title: `Jump to ${sec.type.charAt(0).toUpperCase() + sec.type.slice(1)} Section`,
    category: "Jump to Section" as const,
    icon: BookOpen,
    onSelect: () => {
      editorContext?.setActiveSectionId(sec.id);
      editorContext?.requestNavigation({
        destination: "content",
        destinationType: "workspace",
        source: "command_palette",
      });
      if (!editorContext) {
        navigate("/profile/edit");
      }
    },
  }));

  const commands: CommandItem[] = [
    // Primary Workspaces
    {
      id: "nav-overview",
      title: "Overview Workspace",
      category: "Navigation",
      icon: User,
      onSelect: () => {
        if (editorContext) {
          editorContext.requestNavigation({
            destination: "home",
            destinationType: "workspace",
            source: "command_palette",
          });
        } else {
          navigate("/profile/edit");
        }
      },
    },
    {
      id: "nav-content",
      title: "Content Studio",
      category: "Navigation",
      icon: FileText,
      shortcut: "1",
      onSelect: () => {
        if (editorContext) {
          editorContext.requestNavigation({
            destination: "content",
            destinationType: "workspace",
            source: "command_palette",
          });
        } else {
          navigate("/profile/edit");
        }
      },
    },
    {
      id: "nav-appearance",
      title: "Appearance Studio",
      category: "Navigation",
      icon: Palette,
      shortcut: "2",
      onSelect: () => {
        if (editorContext) {
          editorContext.requestNavigation({
            destination: "design",
            destinationType: "workspace",
            source: "command_palette",
          });
        } else {
          navigate("/profile/edit");
        }
      },
    },
    {
      id: "nav-publish",
      title: "Publish & SEO Settings",
      category: "Navigation",
      icon: Rocket,
      shortcut: "3",
      onSelect: () => {
        if (editorContext) {
          editorContext.requestNavigation({
            destination: "publish",
            destinationType: "workspace",
            source: "command_palette",
          });
        } else {
          navigate("/profile/edit");
        }
      },
    },
    {
      id: "nav-cv",
      title: "Academic CV Builder",
      category: "Navigation",
      icon: Download,
      onSelect: () => {
        if (editorContext) {
          editorContext.requestNavigation({
            destination: "/profile/cv",
            destinationType: "route",
            source: "command_palette",
          });
        } else {
          navigate("/profile/cv");
        }
      },
    },
    {
      id: "nav-billing",
      title: "Billing & Plans",
      category: "Navigation",
      icon: CreditCard,
      onSelect: () => {
        if (editorContext) {
          editorContext.requestNavigation({
            destination: "/profile/billing",
            destinationType: "route",
            source: "command_palette",
          });
        } else {
          navigate("/profile/billing");
        }
      },
    },

    // Dynamic Sections
    ...dynamicSectionCommands,

    // Actions
    ...(editorContext
      ? [
          {
            id: "act-publish-changes",
            title: "Publish Site Changes Live",
            category: "Actions" as const,
            icon: Rocket,
            shortcut: "⇧⌘P",
            onSelect: () => {
              editorContext.handlePublish();
            },
          },
          {
            id: "act-flush-save",
            title: "Save Changes to Draft Now",
            category: "Actions" as const,
            icon: FileText,
            shortcut: "⌘S",
            onSelect: () => {
              editorContext.flushAutosave();
            },
          },
        ]
      : []),

    // Actions
    ...(editorContext?.publishedUrl
      ? [
          {
            id: "act-live-site",
            title: "View Live Academic Website",
            category: "Actions" as const,
            icon: ExternalLink,
            onSelect: () => {
              window.open(editorContext.publishedUrl, "_blank");
            },
          },
        ]
      : []),
    ...(editorContext
      ? [
          {
            id: "act-preview",
            title: editorContext.previewOpen ? "Hide Live Preview" : "Show Live Preview",
            category: "Actions" as const,
            icon: Eye,
            shortcut: "P",
            onSelect: () => {
              editorContext.setPreviewOpen(!editorContext.previewOpen);
            },
          },
        ]
      : []),
    {
      id: "act-theme",
      title: "Toggle Theme (Dark / Light)",
      category: "Actions",
      icon: document.documentElement.classList.contains("dark") ? Sun : Moon,
      onSelect: () => {
        const isDark = document.documentElement.classList.toggle("dark");
        localStorage.setItem("theme", isDark ? "dark" : "light");
      },
    },
  ];

  // Filter commands by search query
  const filteredCommands = commands.filter(
    (c) =>
      c.title.toLowerCase().includes(query.toLowerCase()) ||
      c.category.toLowerCase().includes(query.toLowerCase())
  );

  // Key navigation inside palette
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (filteredCommands.length || 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev === 0 ? filteredCommands.length - 1 : prev - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const active = filteredCommands[selectedIndex];
      if (active) {
        active.onSelect();
        setIsOpen(false);
      }
    }
  };

  // Keep active item in view
  useEffect(() => {
    if (listRef.current) {
      const activeEl = listRef.current.children[selectedIndex] as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 sm:pt-28 px-4 bg-zinc-950/60 backdrop-blur-xs animate-in fade-in duration-150"
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsOpen(false);
      }}
    >
      <div className="w-full max-w-xl rounded-2xl border border-border/80 bg-card shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150 flex flex-col max-h-[70vh]">
        {/* Search Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/70 bg-background/50">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command, section, or jump destination..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border/60">
            ESC
          </span>
        </div>

        {/* Results List */}
        <div ref={listRef} className="overflow-y-auto p-2 space-y-1">
          {filteredCommands.length > 0 ? (
            filteredCommands.map((command, idx) => {
              const Icon = command.icon;
              const isSelected = idx === selectedIndex;
              return (
                <div
                  key={command.id}
                  onClick={() => {
                    command.onSelect();
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-colors text-xs ${
                    isSelected
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-foreground hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon
                      className={`h-4 w-4 shrink-0 ${
                        isSelected ? "text-primary-foreground" : "text-muted-foreground"
                      }`}
                    />
                    <span>{command.title}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        isSelected
                          ? "bg-white/20 text-white"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {command.category}
                    </span>
                    {command.shortcut && (
                      <span
                        className={`text-[10px] font-mono px-1 py-0.5 rounded ${
                          isSelected
                            ? "bg-white/20 text-white"
                            : "bg-muted/80 text-muted-foreground"
                        }`}
                      >
                        {command.shortcut}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-xs text-muted-foreground">
              No matching commands or sections found.
            </div>
          )}
        </div>

        {/* Footer shortcuts helper */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-border/60 bg-muted/30 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>Esc Close</span>
          </div>
          <span>Cmd + K</span>
        </div>
      </div>
    </div>
  );
}
